import { useEffect, useRef } from 'react'
import { Tracking } from '@/services/Tracking/Tracking'
import { datadogLogs, type LogsInitConfiguration } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'

const env = process.env.NEXT_PUBLIC_DATADOG_ENV || 'local'
const isLocalDev = env === 'local'

let version = process.env.NEXT_PUBLIC_DATADOG_VERSION
if (!version) {
  if (isLocalDev) version = 'local'
  else version = `${process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF}_${process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA}`
}

const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID
const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN

const commonDatadogConfig = {
  clientToken: clientToken!,
  env,
  sessionSampleRate: 100,
  service: process.env.NEXT_PUBLIC_DATADOG_SERVICE_NAME,
  site: 'datadoghq.eu',
  version,
  silentMultipleInit: true,
} as const

export function useInitDatadog() {
  const initializedRef = useRef(false)
  useEffect(() => {
    const delay = window.requestIdleCallback ?? window.setTimeout
    delay(() => {
      if (initializedRef.current) return
      initializedRef.current = true
      if (!applicationId || !clientToken) return
      initDatadogLogs()
      initDatadogRum()
    })
  }, [])
}

function initDatadogLogs() {
  const datadogLogsConfig: LogsInitConfiguration = {
    ...commonDatadogConfig,
    forwardErrorsToLogs: true,
    // It's possible to forward all logs at certain level automatically,
    // but this way we don't control potential spam from 3rd party libraries
    // forwardConsoleLogs: ['error'],
  }

  if (isLocalDev) {
    datadogLogsConfig.beforeSend = (event) => {
      if (event.origin === 'logger') {
        if (event.logger?.name === Tracking.LOGGER_NAME) {
          console.log('tracking event', event.message, event)
        } else {
          console.log(event.message)
        }
      }

      // Prevent logs from reaching DD in local development
      return false
    }
  }

  datadogLogs.init(datadogLogsConfig)
}

function initDatadogRum() {
  if (isLocalDev) return

  datadogRum.init({
    ...commonDatadogConfig,
    clientToken: clientToken!,
    applicationId: applicationId!,
    trackUserInteractions: true,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT
      ? [process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT]
      : undefined,
  })
  datadogRum.startSessionReplayRecording()
}
